import { trackRefreshFailureAutoLogoutOnServer } from '@common/analytics-layer/utils/track-auto-logout.server.utils'
import {
  fingerprintRefreshToken,
  resolveAutoLogoutDevice,
  resolveAutoLogoutFlavour,
} from '@common/analytics-layer/tracking/auto-logout-envelope'
import { getConfig } from '../../core-layer/config'
import { useLogger } from '../../core-layer/composables/useLogger'
import { writeAuthCookieOnEvent, clearAuthCookieOnEvent, type SsrAuthEvent } from '../../core-layer/utils/auth-cookie.server.utils'
import type { AuthCookieData } from '../../core-layer/utils/auth-cookie.utils'
import type { RefreshTokenResponse } from '../types'

/**
 * Proactive SSR token refresh — executed by the server auth middleware
 * (`libs/middleware/auth.ts`) BEFORE Nuxt starts rendering a document request.
 *
 * Why this exists:
 * - Refresh token rotation means every successful refresh invalidates the
 *   previous refresh token. When refreshes happen mid-render (from the old
 *   BaseService 401-retry path), Nuxt's streaming response has usually already
 *   flushed headers, so the refreshed `Set-Cookie` never reaches the browser.
 *   The browser then keeps sending the stale (already-rotated) pair on the next
 *   page load, every refresh attempt rejects, and the session is destroyed —
 *   the "frequent logout" loop.
 * - Running the refresh here, once per document request and only when the
 *   access token is expired/near expiry, guarantees the new `catv2` cookie is
 *   written while response headers are still mutable.
 *
 * Failure policy: never throws. Transient failures (network/timeout/5xx) leave
 * the session untouched so client-side recovery can retry. Terminal failures
 * (4xx — refresh token rejected) clear the session immediately so SSR does not
 * render the user as authenticated with a dead cookie.
 */

/** Refresh when the access token is expired or expires within this window. */
/**
 * Proactive refresh window. A token expiring within this span is treated as
 * 'near-expiry' and refreshed ahead of time. Exported so tests derive their
 * fresh/near-expiry fixtures from it instead of hardcoding an offset that
 * silently falls inside the window when this value changes.
 */
export const ACCESS_TOKEN_REFRESH_SKEW_MS = 2 * 60 * 60 * 1000

/**
 * Hard ceiling for the proactive refresh call. This sits on the page-load
 * critical path (middleware before render, boot plugin before app init), so a
 * slow refresh endpoint must fail open fast instead of hanging the page.
 */
const PROACTIVE_REFRESH_TIMEOUT_MS = 2500

/** Outcome of one proactive refresh attempt. */
export type RefreshOutcome =
  | { kind: 'success'; auth: AuthCookieData }
  /** 4xx — the refresh token is rejected server-side; the session is dead. */
  | { kind: 'terminal'; status?: number; error?: string }
  /** Network/timeout/5xx/no-token — session may still be valid; retry later. */
  | { kind: 'transient'; status?: number; error?: string }

/** Decode a JWT payload's `exp` claim (seconds) as epoch ms. Returns null when undecodable. */
export function getJwtExpiryMs(token: string): number | null {
  try {
    const parts = token.split('.')
    if (parts.length < 2 || !parts[1]) {
      return null
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = typeof atob !== 'undefined' ? atob(base64) : Buffer.from(base64, 'base64').toString('utf-8')
    const payload = JSON.parse(json) as { exp?: unknown }
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

/** True when the access token is expired or within the refresh skew window. */
export function isAccessTokenExpiredOrNearExpiry(token: string, nowMs: number = Date.now()): boolean {
  return getRefreshTrigger(token, nowMs) !== null
}

/**
 * Classify the access token:
 * - 'expired' — past `exp`; all callers MUST block on the refresh (token is useless).
 * - 'near-expiry' — inside the skew window; token still works. Server middleware
 *   still blocks (its Set-Cookie must land before render streams headers); only
 *   the client boot plugin may refresh in the background (client cookie writes
 *   have no header-flush race).
 * - null — fresh, or undecodable (do not refresh on every request).
 */
export function getRefreshTrigger(token: string, nowMs: number = Date.now()): 'expired' | 'near-expiry' | null {
  const expiryMs = getJwtExpiryMs(token)
  if (expiryMs === null) {
    return null
  }
  if (expiryMs <= nowMs) {
    return 'expired'
  }
  return expiryMs - nowMs <= ACCESS_TOKEN_REFRESH_SKEW_MS ? 'near-expiry' : null
}

/**
 * Build the refresh endpoint URL, mirroring `BaseService` app-specific rewrites:
 * - ClientPortal: `/clientclub/` → `/clientportal-middleware/clientclub/`.
 * - GoKollab/Courses: direct clientclub URL.
 * - Meet: returns null — Auth V1 vs V2 is only resolvable via the location store
 *   (`isAuthV2Enabled`), which is not available at nitro middleware level. V1 needs
 *   `/auth/refresh-token` + a `token` header, V2 needs the clientportal-middleware
 *   path; guessing wrong breaks the refresh. Meet SSR therefore skips proactive
 *   refresh and relies on the client-side refresh (which has the correct branch).
 */
function buildRefreshUrl(locationId: string): string | null {
  const appName = (process.env.APP_NAME || '').toUpperCase()
  if (appName === 'MEET') {
    return null
  }
  const base = getConfig().CLIENTPORTAL_SERVER_URL.replace(/\/$/, '')
  const url = `${base}/v2/${locationId}/auth/refresh-token`
  if (appName === 'CLIENTPORTAL') {
    return url.replace('/clientclub/', '/clientportal-middleware/clientclub/')
  }
  return url
}

function encodeAuthCookieValue(data: AuthCookieData): string {
  const json = JSON.stringify(data)
  return typeof btoa !== 'undefined' ? btoa(json) : Buffer.from(json).toString('base64')
}

/**
 * Perform one refresh HTTP call. Context-free (no Pinia, no location store) —
 * safe from nitro middleware and early client plugins alike. Never throws.
 * Returns a classified outcome so callers can clear dead sessions on terminal
 * (4xx) rejections while preserving the cookie across transient failures.
 */
export async function performRefresh(authData: AuthCookieData): Promise<RefreshOutcome> {
  const { logger } = useLogger()

  if (!authData.refreshToken || !authData.locationId) {
    logger.warn('[SSR Auth] Skipping proactive refresh: missing refreshToken or locationId', {
      labels: { context: 'ssr-token-refresh' },
    })
    return { kind: 'transient' }
  }

  const url = buildRefreshUrl(authData.locationId)
  if (!url) {
    // Meet: V1/V2 not resolvable here — skip SSR refresh, client recovers.
    return { kind: 'transient' }
  }

  try {
    logger.info('[SSR Auth] Proactive refresh attempt', {
      labels: { context: 'ssr-token-refresh', phase: 'http', url },
    })
    const data = await $fetch<RefreshTokenResponse>(url, {
      method: 'POST',
      body: { refreshToken: authData.refreshToken },
      headers: {
        isIAM: 'true',
        channel: 'APP',
        source: 'PORTAL_USER',
        version: '2023-02-21',
      },
      timeout: PROACTIVE_REFRESH_TIMEOUT_MS,
      retry: 0,
    })

    if (!data?.token) {
      logger.warn('[SSR Auth] Proactive refresh returned no access token', {
        labels: { context: 'ssr-token-refresh' },
      })
      return { kind: 'transient' }
    }

    logger.info('[SSR Auth] Proactive token refresh succeeded', {
      labels: { context: 'ssr-token-refresh' },
    })
    return {
      kind: 'success',
      auth: {
        ...authData,
        token: data.token,
        refreshToken: data.refreshToken || authData.refreshToken,
        ...(data.sessionUniqueKey ? { sessionUniqueKey: data.sessionUniqueKey } : {}),
      },
    }
  } catch (error) {
    const status =
      (error as { status?: number } | undefined)?.status ?? (error as { response?: { status?: number } } | undefined)?.response?.status
    const terminal = typeof status === 'number' && status >= 400 && status < 500
    logger.warn('[SSR Auth] Proactive token refresh failed', {
      labels: {
        context: 'ssr-token-refresh',
        outcome: terminal ? 'terminal' : 'transient',
        http_status: status === undefined ? 'none' : String(status),
      },
      error: error instanceof Error ? error : new Error(String(error)),
    })
    // Status and message are surfaced (not discarded) so the terminal branch can report WHY the
    // session died in the auto-logout diagnostic.
    return {
      kind: terminal ? 'terminal' : 'transient',
      ...(status === undefined ? {} : { status }),
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

/** Write the rotated cookie on THIS request's response + context (headers still mutable pre-render). */
function applyRefreshedAuth(event: SsrAuthEvent, updatedAuth: AuthCookieData): void {
  writeAuthCookieOnEvent(event, encodeAuthCookieValue(updatedAuth))
  if (event.context) {
    event.context.auth = updatedAuth
  }
}

/**
 * Emits the auto-logout diagnostic for a terminal (4xx) pre-render refresh.
 *
 * This branch clears `catv2` before render, which is a genuine auto-logout that would otherwise
 * be invisible. Fully guarded: `maybeRefreshAuthTokenOnEvent` is called from
 * `libs/middleware/auth.ts`, whose contract is "must never throw".
 */
function reportTerminalAutoLogout(event: SsrAuthEvent, outcome: { status?: number; error?: string }, authData: AuthCookieData): void {
  try {
    trackRefreshFailureAutoLogoutOnServer({
      locationId: authData.locationId,
      contactId: authData.contactId,
      flavour: resolveAutoLogoutFlavour(process.env.APP_NAME),
      device: resolveAutoLogoutDevice(event.node?.req?.headers?.['user-agent']),
      refresh: {
        ...(outcome.status === undefined ? {} : { statusCode: outcome.status }),
        error: outcome.error ?? 'Proactive refresh rejected',
        fingerprint: fingerprintRefreshToken(authData.refreshToken),
      },
    })
  } catch (error) {
    const { logger } = useLogger()
    logger.warn('[SSR Auth] Auto-logout analytics emit failed; continuing to clear the session', {
      labels: { context: 'ssr-token-refresh', phase: 'analytics' },
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }
}

/**
 * Apply a refresh outcome to the request: success writes the rotated cookie +
 * context; terminal (4xx) clears the dead session so SSR does not render the
 * user as authenticated; transient leaves everything for client-side recovery.
 * Guarded against writes after headers flushed — a late Set-Cookie must not
 * throw, but it IS logged: a swallowed rotation leaves the browser holding a
 * rotated-away refresh token, and the next load 400s into a logout.
 */
function applyOutcome(event: SsrAuthEvent, outcome: RefreshOutcome, authData: AuthCookieData): void {
  const { logger } = useLogger()
  try {
    if (outcome.kind === 'success') {
      applyRefreshedAuth(event, outcome.auth)
    } else if (outcome.kind === 'terminal') {
      // Report BEFORE clearing: this is a real auto-logout, and the identity plus refresh token
      // it describes only exist until the cookie and context are wiped below. Self-guarded so a
      // failing diagnostic can never skip the clear.
      reportTerminalAutoLogout(event, outcome, authData)
      clearAuthCookieOnEvent(event)
      if (event.context) {
        delete event.context.auth
      }
    }
    logger.info('[SSR Auth] Proactive refresh applied', {
      labels: { context: 'ssr-token-refresh', phase: 'apply', outcome: outcome.kind },
    })
  } catch (error) {
    logger.warn('[SSR Auth] Cookie write swallowed — response headers already flushed; browser keeps stale cookie', {
      labels: { context: 'ssr-token-refresh', phase: 'apply', outcome: outcome.kind },
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }
}

/**
 * Refresh `event.context.auth` when its access token is expired/near expiry.
 * ALWAYS awaited — never backgrounded. A backgrounded refresh finishes after
 * the streaming response flushed headers, so its rotated Set-Cookie is
 * silently dropped: the browser keeps the (now rotated-away) refresh token,
 * the next load 400s, and the terminal policy destroys the session. Blocking
 * here is bounded by PROACTIVE_REFRESH_TIMEOUT_MS and rare (2min skew window
 * per 24h token).
 *
 * No dedupe/single-flight here by design: cross-request rotation races are the
 * backend's responsibility (rotation leeway window on the refresh endpoint).
 */
export async function maybeRefreshAuthTokenOnEvent(event: SsrAuthEvent): Promise<void> {
  const { logger } = useLogger()
  const authData = event.context?.auth
  const trigger = authData?.token ? getRefreshTrigger(authData.token) : null
  if (!authData || !trigger) {
    logger.info('[SSR Auth] Proactive refresh skipped', {
      labels: { context: 'ssr-token-refresh', phase: 'check', reason: authData?.token ? 'fresh-token' : 'no-auth' },
    })
    return
  }

  applyOutcome(event, await performRefresh(authData), authData)
}
